// The flow's final screen. Two modes, decided by the carrier's signing
// shape: a carrier with its own document packet (Global Atlantic) gets
// the "one more signature" walkthrough — their packet arrives by email
// and the plan isn't final until it's signed; every other carrier gets
// the simple "we're reviewing your application" close. A signed-out visitor
// is offered the account station.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { CONTINUE_BUTTON_CLASS } from "components/FormV2";

import { finishedPolicyDestination } from "./account";
import { fetchFinalizeView, type FinalizeView } from "./api";
import LoadingScreen from "./LoadingScreen";
import { dollars } from "./payment-copy";
import { stationPath } from "./step-routes";

const LOADING_STATUSES = ["Gathering your confirmation…"] as const;

const BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  "american-express": "American Express",
  discover: "Discover",
  "diners-club": "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
};

interface NextStep {
  title: string;
  detail: string;
  strong?: string;
}

function packetSteps(carrierName: string): NextStep[] {
  return [
    {
      title: `Sign ${carrierName}'s copy`,
      detail:
        "There are four or five signatures, and it takes a couple of minutes. Most are already filled in.",
      strong: "Your plan isn't final until this is signed.",
    },
    {
      title: "A licensed agent checks everything and submits it",
      detail:
        "Usually the same day. If anything needs clarifying, they'll call you.",
    },
    {
      title: "Your policy documents and ID card arrive by mail",
      detail: `${carrierName} issues your policy number and mails the packet, which takes about four to five weeks.`,
    },
    {
      title: "Share your plan with your family",
      detail:
        "Your digital copies are already in your inbox. Forward them, with our 24/7 number, to whoever will need to make the call.",
    },
  ];
}

function reviewSteps(carrierName: string): NextStep[] {
  return [
    {
      title: "A licensed agent checks everything and submits it",
      detail: `Usually the same day — your application goes to ${carrierName}, the carrier that backs your plan. If anything needs clarifying, they'll call you.`,
    },
    {
      title: "Your policy documents arrive by mail",
      detail: `${carrierName} issues your policy number and mails your documents once the application is approved.`,
    },
    {
      title: "Share your plan with your family",
      detail:
        "Your digital copies are already in your inbox. Forward them, with our 24/7 number, to whoever will need to make the call.",
    },
  ];
}

function CheckIcon() {
  return (
    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-evergreen">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="h-7 w-7 text-white"
        aria-hidden="true"
      >
        <path
          d="M5 13l4 4L19 7"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function ReceiptLine({ view }: { view: FinalizeView }) {
  const amount = view.charge.amount_cents ?? 0;
  const card = view.payment_method;
  const brand = card ? (BRAND_LABELS[card.brand ?? ""] ?? "card") : null;
  return (
    <p className="mt-6 text-sm text-oat-700">
      {amount > 0 ? (
        <>
          Charged {dollars(amount)}
          {card ? (
            <>
              {" "}
              to {brand} ending{" "}
              <span className="ph-no-capture">{card.last_4 ?? "····"}</span>
            </>
          ) : null}
        </>
      ) : (
        "Nothing was charged today"
      )}{" "}
      · Copies of your signed documents are in your inbox
    </p>
  );
}

function NextStepsCard({
  steps,
  heading,
}: {
  steps: NextStep[];
  heading: string;
}) {
  return (
    <div className="mt-8 rounded-xl border border-oat-300 bg-white text-left">
      <p className="px-5 pt-5 text-[13px] font-bold uppercase tracking-[0.25em] text-evergreen">
        {heading}
      </p>
      <div className="divide-y divide-oat-200">
        {steps.map((step, index) => (
          <div key={step.title} className="flex gap-4 px-5 py-4">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-oat text-sm font-bold text-soil">
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="text-base font-bold text-soil">{step.title}</p>
              <p className="mt-0.5 text-sm leading-relaxed text-oat-700">
                {step.detail}
                {step.strong ? (
                  <>
                    {" "}
                    <span className="font-bold text-soil">{step.strong}</span>
                  </>
                ) : null}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PacketMode({
  view,
  firstName,
}: {
  view: FinalizeView;
  firstName: string | null;
}) {
  const carrier = view.carrier?.name ?? "your insurance carrier";
  const paid = (view.charge.amount_cents ?? 0) > 0;
  return (
    <>
      <CheckIcon />
      <h1 className="mt-6 font-serif text-3xl text-soil">
        {firstName ? (
          <>
            Almost there, <span className="ph-no-capture">{firstName}</span>
            .{" "}
          </>
        ) : (
          "Almost there. "
        )}
        <span className="font-sans font-bold text-evergreen">
          One more signature.
        </span>
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        {paid
          ? "Your payment went through and your plan is locked in at today's price. "
          : "Your plan is locked in at today's price. "}
        The insurance company that backs your plan needs your signature on their
        own copy.
      </p>

      <div className="mt-8 rounded-xl bg-oat-50 px-5 py-5 text-left">
        <p className="text-base font-bold text-evergreen">
          Look for a second email — from {carrier}, not from us
        </p>
        <p className="mt-1 text-base leading-relaxed text-oat-800">
          It usually arrives within 1–3 business days at the email address on
          your application. It will look different from ours, because it comes
          from them. Same plan, same price, nothing new to decide.
        </p>
      </div>

      <NextStepsCard heading="What happens next" steps={packetSteps(carrier)} />

      <div className="mt-8 text-left">
        <p className="text-[13px] font-bold uppercase tracking-[0.25em] text-evergreen">
          Why you sign twice
        </p>
        <p className="mt-2 text-base leading-relaxed text-oat-700">
          Your money isn&rsquo;t sitting in our bank account, and by law it
          can&rsquo;t be. It goes to {carrier}, who hold it until it&rsquo;s
          needed. That&rsquo;s why they need your signature on their own
          contract, not just ours.
        </p>
        <p className="mt-2 text-base leading-relaxed text-oat-700">
          <span className="font-bold text-soil">
            That&rsquo;s the protection.
          </span>{" "}
          If anything ever happened to After, your plan wouldn&rsquo;t go with
          us. The money is in your policy, and your family could take a full
          refund or move the plan to another funeral home to carry out the same
          arrangements.
        </p>
      </div>

      <ReceiptLine view={view} />
    </>
  );
}

function ReviewMode({
  view,
  firstName,
}: {
  view: FinalizeView;
  firstName: string | null;
}) {
  const carrier = view.carrier?.name ?? "your insurance carrier";
  return (
    <>
      <CheckIcon />
      <h1 className="mt-6 font-serif text-3xl text-soil">
        {firstName ? (
          <>
            That&rsquo;s everything,{" "}
            <span className="ph-no-capture">{firstName}</span>.
          </>
        ) : (
          "That's everything."
        )}
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        We&rsquo;re reviewing your application and will submit it to {carrier}.
        There&rsquo;s nothing more you need to do today.
      </p>

      <NextStepsCard heading="What happens next" steps={reviewSteps(carrier)} />

      <ReceiptLine view={view} />
    </>
  );
}

function CreateAccountCard({ quoteId }: { quoteId: string }) {
  return (
    <div className="mt-8 rounded-xl bg-oat-50 px-5 py-5 text-left">
      <p className="text-base font-bold text-evergreen">Create your account</p>
      <p className="mt-1 text-base leading-relaxed text-oat-800">
        Your policy, signed documents, and payment history live in your account.
      </p>
      <Link
        to={stationPath(quoteId, "account")}
        className={`${CONTINUE_BUTTON_CLASS} mt-4`}
      >
        Create your account
      </Link>
    </div>
  );
}

function ViewPolicyCard({ policyId }: { policyId: string | null }) {
  const destination = finishedPolicyDestination(policyId);
  return (
    <div className="mt-8 text-center">
      <Link to={destination.to} className={CONTINUE_BUTTON_CLASS}>
        {destination.label}
      </Link>
    </div>
  );
}

export default function SuccessView({
  quoteId,
  firstName,
  offerAccount,
  signedIn,
}: {
  quoteId: string;
  firstName: string | null;
  /** The visitor is signed out and the quote can start an account. */
  offerAccount: boolean;
  /** The visitor is signed in: they are offered their policy. */
  signedIn: boolean;
}) {
  const [view, setView] = useState<FinalizeView | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchFinalizeView(quoteId)
      .then((loaded) => {
        if (!cancelled) setView(loaded);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  if (failed) {
    // The milestone already settled server-side — a confirmation that
    // cannot load still deserves a real close, not an error wall.
    return (
      <main className="mx-auto w-full max-w-screen-sm flex-1 px-5 pb-16 pt-16 text-center">
        <CheckIcon />
        <h1 className="mt-6 font-serif text-3xl text-soil">
          That&rsquo;s everything
        </h1>
        <p className="mt-3 text-lg leading-relaxed text-oat-700">
          We&rsquo;re reviewing your application and will keep you posted.
        </p>
        {offerAccount && <CreateAccountCard quoteId={quoteId} />}
        {signedIn && <ViewPolicyCard policyId={null} />}
      </main>
    );
  }

  if (!view) {
    return (
      <LoadingScreen
        kicker="All set"
        titleA="Gathering your"
        titleB="confirmation"
        statuses={LOADING_STATUSES}
      />
    );
  }

  return (
    <main className="mx-auto w-full max-w-screen-sm flex-1 px-5 pb-16 pt-16 text-center">
      {view.carrier?.requires_separate_document_packet ? (
        <PacketMode view={view} firstName={firstName} />
      ) : (
        <ReviewMode view={view} firstName={firstName} />
      )}
      {offerAccount && <CreateAccountCard quoteId={quoteId} />}
      {signedIn && <ViewPolicyCard policyId={view.policy_id ?? null} />}
    </main>
  );
}
